import { useEffect, useMemo, useState } from 'react';
import { Download, Eye, FileText, Loader2, Menu, Pencil, X } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import api, { DocumentSignFileResponse } from '@/lib/api';
import { useDeleteDocumentFile, useRenameDocumentFile } from '../hooks';
import { PdfPageView, usePdfDocument } from './PdfPageView';

interface DocumentGridProps {
  envelopeId: string;
  files: DocumentSignFileResponse[];
  /** Disable mutating actions (used when the envelope is no longer a draft). */
  readOnly?: boolean;
}

/** Inner content width of a card (`w-44` 176px minus the 1px border each side). */
const THUMB_WIDTH = 174;

/**
 * Grid of uploaded source documents. Each tile is a square preview of the
 * PDF's first page with a close button (remove) in the top-right corner and
 * a menu (view / download / rename) in the bottom-right.
 *
 * Sealed / certificate files are filtered out — this surface is the draft
 * builder's source-document tray.
 */
export function DocumentGrid({ envelopeId, files, readOnly = false }: DocumentGridProps) {
  const sourceFiles = useMemo(() => files.filter((f) => f.kind === 'source'), [files]);

  if (sourceFiles.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-4">
      {sourceFiles.map((file) => (
        <DocumentCard key={file.fileId} envelopeId={envelopeId} file={file} readOnly={readOnly} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

interface DocumentCardProps {
  envelopeId: string;
  file: DocumentSignFileResponse;
  readOnly: boolean;
}

function DocumentCard({ envelopeId, file, readOnly }: DocumentCardProps) {
  const deleteFile = useDeleteDocumentFile(envelopeId);
  const renameFile = useRenameDocumentFile(envelopeId);

  const [thumbUrl, setThumbUrl] = useState<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const { doc, error: pdfError } = usePdfDocument(thumbUrl);

  // Fetch a signed URL just for the first-page thumbnail. Best-effort — if it
  // fails the tile falls back to a generic file icon.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { downloadUrl } = await api.getDocumentSignFileDownloadUrl(envelopeId, file.fileId);
        if (!cancelled) setThumbUrl(downloadUrl);
      } catch {
        // Swallow — the fallback icon covers it.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [envelopeId, file.fileId]);

  // Download / view both open a fresh signed URL in a new tab; only the
  // Content-Disposition differs (attachment forces a save, inline displays).
  async function openWith(disposition: 'attachment' | 'inline') {
    setActionBusy(true);
    try {
      const { downloadUrl } = await api.getDocumentSignFileDownloadUrl(
        envelopeId,
        file.fileId,
        disposition,
      );
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
    } finally {
      setActionBusy(false);
    }
  }

  function handleRename(fileName: string) {
    setRenameError(null);
    renameFile.mutate(
      { fileId: file.fileId, fileName },
      {
        onSuccess: () => setRenameOpen(false),
        onError: (err) =>
          setRenameError(err instanceof Error ? err.message : 'Could not rename the document.'),
      },
    );
  }

  return (
    <div className="flex w-44 flex-col gap-1.5">
      <div className="relative h-44 w-44 overflow-hidden rounded-lg border bg-muted">
        {doc ? (
          <PdfPageView pdfDoc={doc} pageNumber={1} width={THUMB_WIDTH} />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            {pdfError ? (
              <FileText className="h-10 w-10 text-muted-foreground" aria-hidden="true" />
            ) : (
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden="true" />
            )}
          </div>
        )}

        {!readOnly && (
          <button
            type="button"
            onClick={() => setConfirmDeleteOpen(true)}
            disabled={deleteFile.isPending}
            aria-label={`Remove ${file.fileName}`}
            className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md border bg-background/90 text-muted-foreground shadow-sm transition-colors hover:bg-destructive hover:text-destructive-foreground disabled:opacity-50"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              disabled={actionBusy}
              aria-label={`Actions for ${file.fileName}`}
              className="absolute bottom-1.5 right-1.5 inline-flex h-7 w-7 items-center justify-center rounded-md border bg-background/90 text-muted-foreground shadow-sm transition-colors hover:bg-accent hover:text-accent-foreground disabled:opacity-50"
            >
              <Menu className="h-4 w-4" aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => void openWith('attachment')}>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Download
            </DropdownMenuItem>
            {!readOnly && (
              <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                <Pencil className="mr-2 h-4 w-4" aria-hidden="true" />
                Rename
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => void openWith('inline')}>
              <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
              View document
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <span
        className="block w-44 truncate text-center text-xs text-muted-foreground"
        title={file.fileName}
      >
        {file.fileName}
      </span>

      <RenameDialog
        open={renameOpen}
        onOpenChange={(next) => {
          setRenameOpen(next);
          if (!next) setRenameError(null);
        }}
        currentName={file.fileName}
        isPending={renameFile.isPending}
        error={renameError}
        onRename={handleRename}
      />

      <AlertDialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this document?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{file.fileName}&rdquo; and any fields placed on it will be removed from this
              envelope. You can upload it again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteFile.mutate(file.fileId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rename dialog
// ---------------------------------------------------------------------------

interface RenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentName: string;
  isPending: boolean;
  error: string | null;
  onRename: (fileName: string) => void;
}

function RenameDialog({
  open,
  onOpenChange,
  currentName,
  isPending,
  error,
  onRename,
}: RenameDialogProps) {
  const [name, setName] = useState(currentName);

  // Reset the field to the current name each time the dialog opens.
  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Rename document</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const trimmed = name.trim();
            if (trimmed) onRename(trimmed);
          }}
          className="space-y-3"
        >
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={300}
            autoFocus
            aria-label="Document name"
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !name.trim()}>
              {isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
